'use client';

import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isAccountCacheStale, readAccountCache } from '@/lib/customer/account-prefetch';
import Link from 'next/link';
import {
 ArrowRight,
 CheckCircle2,
 ChefHat,
 Clock,
 Gift,
 Loader2,
 LogOut,
Package,
  ReceiptText,
  ShieldCheck,
 User,
 Utensils,
 X,
} from 'lucide-react';
import { toast } from 'sonner';

import { ContentSkeleton } from '@/components/ui/content-skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
 Dialog,
 DialogContent,
 DialogDescription,
 DialogHeader,
 DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toHslTriplet } from '@/lib/menu-themes/hsl';
import { cn } from '@/components/ui/utils';
import { useCustomerOrderStatus } from '@/hooks/customer/useCustomerOrderStatus';
import { PickupApprovalStatus } from '@/components/menu/pickup-approval/PickupApprovalStatus';
import { pickupApprovalGovernsStatus } from '@/lib/customer/guest-orders';
import { chowAppUrl, useCustomerSession } from '@/lib/customer-bridge';
import { getGuestUsername } from '@/lib/customer/guest-identity';
import { CustomerAuthModal } from '@/components/customer-surface/CustomerAuthModal';
import { resolveReadableTextColor, type TemplateSurfacePalette } from '@/app/dashboard/templates/components/lib/templateExperience';
import { formatMoney as formatMoneyCanonical } from '@/lib/money';
import {
  orderStatusChip,
  orderStatusLabel,
  type StatusVocabularyContext,
} from '@/lib/order-status-vocabulary';

// ── Inline order status (mini version for use inside the panel Dialog) ──────

// Keys are the engine's; the words come from the vocabulary layer (doc 04 §5).
const STATUS_STEPS = [
 { key: 'placed', icon: Clock },
 { key: 'preparing', icon: ChefHat },
 { key: 'ready', icon: Package },
 { key: 'served', icon: CheckCircle2 },
] as const;

/** The service mode the order carries, narrowed to what the vocabulary keys on.
 *  Uses v2 `orderIntent` when available, falls back to legacy `serviceMode`. */
function guestFulfilment(
  orderIntent: string | null | undefined,
  serviceMode: string | null | undefined,
): StatusVocabularyContext['fulfilment'] {
  if (orderIntent === 'DINE_IN') return 'dine_in';
  if (orderIntent === 'PICKUP') return 'pickup';
  if (orderIntent === 'DELIVERY') return 'delivery';
  if (serviceMode === 'pickup' || serviceMode === 'counter_pickup') return 'pickup';
  if (serviceMode === 'delivery') return 'delivery';
  if (serviceMode === 'dine_in' || serviceMode === 'table') return 'dine_in';
  return null;
}

/** A rail caption. Short guest wording (doc 04 §5) — never the enum. */
function stepCaption(key: string, fulfilment: StatusVocabularyContext['fulfilment']) {
  // `served` is the rail's last stop and has no guest wording of its own —
  // from the guest's side the order is simply done.
  if (key === 'served') return orderStatusChip('completed') ?? 'Done';
  return orderStatusChip(key, { fulfilment }) ?? 'In progress';
}


function stepIndex(status: string) {
  const map: Record<string, number> = { placed: 0, pending: 0, accepted: 0, awaiting_prepayment: 0, preparing: 1, ready: 2, served: 3, awaiting_payment: 3, paid: 3, completed: 3 };
  return map[status] ?? 0;
}

// Delegates to lib/money.ts so every surface prints a price identically.
// Previously used the *guest's* browser locale (D3, docs/qa/production-demo-readiness-2026-08-13.md).
function fmt(v: number, currency: string | null) {
 return formatMoneyCanonical(v, currency);
}

function InlineOrderStatusContent({
  orderId,
  accessToken,
  onStartOver,
}: {
  orderId: string;
  accessToken: string;
  /** Close the dialog and leave the guest on the menu, cart intact (doc 07 §5). */
  onStartOver: () => void;
}) {
  const { order, loading, error } = useCustomerOrderStatus(orderId, accessToken);

  if (loading) {
  return <ContentSkeleton variant="inline" />;
  }

  if (error || !order) {
  return (
  <p className="py-8 text-center text-sm text-[var(--color-text-secondary)]">
  {error || 'Could not load this order.'}
  </p>
  );
  }

const isCancelled = order.status === 'cancelled';
  // Same switch as the inline tracker and the full-page tracker: while the
  // counter's verdict is what this guest is waiting on, the hero and rail
  // describe a journey that has not begun, and the payment window is running.
  const approvalActive = pickupApprovalGovernsStatus(order);
  // "Done" only when fulfilled AND settled (or ended). `served` alone is not
  // done: a dine-in order that has been served still owes payment.
  const isDone =
    order.status === 'completed' ||
    order.status === 'paid' ||
    (order.status === 'served' && order.paymentStatus === 'paid');
  const billOpen = order.status === 'awaiting_payment' || (order.status === 'served' && order.paymentStatus !== 'paid');
  const idx = stepIndex(order.status);

return (
  <div className="space-y-4 p-4">
  {approvalActive ? (
  <PickupApprovalStatus
  order={{
  status: order.status,
  metadata: order.pickupApproval,
  total: order.total,
  currency: order.currency,
  }}
  branchName={order.branchName}
  etaLabel={order.eta?.visible ? order.eta.label : null}
  formatTotal={(amount, currency) => fmt(amount, currency)}
  onPay={() => { window.location.href = `/pay/${orderId}`; }}
  onStartOver={onStartOver}
  />
  ) : (
  <>
  {/* Status hero */}
<div
  className={cn(
  'rounded-2xl border p-4 text-center',
  isCancelled
  ? 'border-destructive/30 bg-destructive/5'
  : isDone
  ? 'border-[var(--color-success)]/20 bg-[var(--color-success-soft)]'
  : 'border-[var(--pub-border-subtle)] bg-[var(--color-bg-sunken)]/30',
  )}
  aria-live="polite"
  >
{isCancelled ? (
  <p className="font-semibold text-destructive">Cancelled</p>
  ) : isDone ? (
  <>
  <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-[var(--color-success)]" />
  <p className="font-semibold text-[var(--color-success)]">All done!</p>
  </>
  ) : billOpen ? (
  <>
  <ReceiptText className="mx-auto mb-2 h-8 w-8 text-[var(--color-text-secondary)]" />
  <p className="font-semibold">{order.serviceMode === 'pickup' || order.serviceMode === 'counter_pickup' ? 'Ready for pickup' : 'Enjoy your meal'}</p>
  <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
  {order.serviceMode === 'pickup' || order.serviceMode === 'counter_pickup'
  ? 'Your bill is open — pay at the counter when you collect.'
  : 'Your bill is open — settle it with staff when you are ready.'}
  </p>
  </>
  ) : (
 <>
 <Utensils className="mx-auto mb-2 h-8 w-8 animate-pulse text-[var(--color-warning)]" />
 <p className="font-semibold">{orderStatusLabel(order.status, 'guest', { fulfilment: guestFulfilment(order.orderIntent, order.serviceMode) })}</p>
 <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
 {order.eta?.visible ? order.eta.label : 'Updates automatically'}
 </p>
 </>
 )}
 </div>

 {/* Progress steps */}
 {!isCancelled && (
 <div className="flex items-center">
 {STATUS_STEPS.map((step, i) => {
 const Icon = step.icon;
 const done = i <= idx;
 const active = i === idx;
 return (
 <div key={step.key} className="flex flex-1 flex-col items-center gap-1">
 <div className="relative flex w-full items-center">
 {i > 0 && <div className={cn('h-0.5 flex-1', done ? 'bg-[var(--color-warning)]' : 'bg-border')} />}
 <div
 className={cn(
 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-all',
 active
 ? 'scale-110 border-[var(--color-warning)] bg-[var(--color-warning)] text-white'
 : done
 ? 'border-[var(--color-warning)] bg-[var(--color-warning)] text-white'
 : 'border-[var(--pub-border-subtle)] bg-[var(--color-bg-sunken)] text-[var(--color-text-secondary)]',
 )}
 >
 <Icon className="h-3 w-3" />
 </div>
 {i < STATUS_STEPS.length - 1 && (
 <div className={cn('h-0.5 flex-1', i < idx ? 'bg-[var(--color-warning)]' : 'bg-border')} />
 )}
 </div>
 <span
 className={cn(
 'text-center text-2xs font-medium',
 active ? 'text-[var(--color-warning)]' : done ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]',
 )}
 >
 {stepCaption(step.key, guestFulfilment(order.orderIntent, order.serviceMode))}
 </span>
 </div>
 );
 })}
</div>
  )}
  </>
  )}

  {/* Totals */}
 <div className="space-y-1.5 rounded-xl border bg-card px-3 py-2.5 text-sm">
 {order.tableLabel && (
 <div className="flex justify-between">
 <span className="text-[var(--color-text-secondary)]">Table</span>
 <span className="font-medium">{order.tableLabel}</span>
 </div>
 )}
 {order.taxTotal ? (
 <div className="flex justify-between">
 <span className="text-[var(--color-text-secondary)]">Subtotal</span>
 <span>{fmt(order.subtotal, order.currency)}</span>
 </div>
 ) : null}
 {order.taxTotal ? (
 <div className="flex justify-between">
 <span className="text-[var(--color-text-secondary)]">Tax</span>
 <span>{fmt(order.taxTotal, order.currency)}</span>
 </div>
 ) : null}
 {order.serviceChargeTotal ? (
 <div className="flex justify-between">
 <span className="text-[var(--color-text-secondary)]">Service charge</span>
 <span>{fmt(order.serviceChargeTotal, order.currency)}</span>
 </div>
 ) : null}
 {order.discountTotal ? (
 <div className="flex justify-between">
 <span className="text-[var(--color-text-secondary)]">Discount</span>
 <span className="text-[var(--color-success)]">-{fmt(order.discountTotal, order.currency)}</span>
 </div>
 ) : null}
 <div className="flex justify-between border-t pt-1.5 font-bold">
 <span>Total</span>
 <span>{fmt(order.total, order.currency)}</span>
 </div>
 </div>
 </div>
 );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function initialsFor(name: string | null | undefined, email: string | null | undefined): string {
 const source = name || email || '';
 const parts = source.trim().split(/\s+/).filter(Boolean);
 if (parts.length === 0) return '?';
 if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
 return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

// ── Types ────────────────────────────────────────────────────────────────────

type LinkedLoyaltyAccount = { businessId: string; businessName: string; balance: number; linkedAt: string };
type LinkableLoyaltyAccount = { businessId: string; businessName: string; loyaltyAccountId: string; balance: number };
type SavedGiftCard = {
 giftCardId: string;
 code: string;
 businessName: string;
 balance: number;
 currency: string | null;
 status: 'active' | 'cancelled';
};
type CustomerOrder = {
 serviceOrderId: string;
 orderNumber: string;
 status: string;
 businessName: string;
 total: number;
 currency: string | null;
 accessToken: string;
};

// ── Main component ────────────────────────────────────────────────────────────

export function CustomerAccountAffordance({
 businessSlug = null,
 businessId = null,
 palette,
 chow = false,
}: {
 businessSlug?: string | null;
 /**
  * The business whose page this panel is rendered on.
  *
  * Supplied, the panel shows the guest's history and loyalty *here*. Omitted,
  * it falls back to everything — which is right on the customer dashboard and
  * wrong on a merchant's own menu, where it listed other restaurants by name.
  */
 businessId?: string | null;
 palette?: TemplateSurfacePalette;
 chow?: boolean;
}) {
 const { data: session, status, signOut, refresh, customerFetch } = useCustomerSession();
 const [authOpen, setAuthOpen] = useState(false);
 const [linked, setLinked] = useState<LinkedLoyaltyAccount[]>([]);
 const [candidates, setCandidates] = useState<LinkableLoyaltyAccount[]>([]);
 const [confirmingId, setConfirmingId] = useState<string | null>(null);
 const [giftCards, setGiftCards] = useState<SavedGiftCard[]>([]);
 const [giftCardCode, setGiftCardCode] = useState('');
 const [giftCardBusy, setGiftCardBusy] = useState<'save' | string | null>(null);
 const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [trackedOrder, setTrackedOrder] = useState<{ id: string; token: string } | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  // Read in an effect (not render) so the server-rendered panel and the
  // hydrated client never disagree about the guest handle from localStorage.
  const [guestUsername, setGuestUsername] = useState<string | null>(null);
  useEffect(() => {
  if (status !== 'authenticated') setGuestUsername(getGuestUsername());
  }, [status]);

  const safePalette = useMemo<TemplateSurfacePalette | null>(() => {
  if (!palette) return null;
  return {
  ...palette,
 foreground: resolveReadableTextColor(palette.background, palette.foreground),
 cardForeground: resolveReadableTextColor(palette.card, palette.cardForeground || palette.foreground),
 mutedForeground: resolveReadableTextColor(palette.muted, palette.mutedForeground || palette.foreground),
 accentForeground: resolveReadableTextColor(palette.accent, palette.accentForeground),
 primaryForeground: resolveReadableTextColor(palette.primary, palette.primaryForeground || palette.accentForeground),
 promoText: resolveReadableTextColor(palette.promoBg, palette.promoText || palette.accent),
 };
 }, [palette]);

 const dialogStyle: CSSProperties | undefined = safePalette
 ? {
 '--background': toHslTriplet(safePalette.card),
 '--foreground': toHslTriplet(safePalette.cardForeground),
 '--card': toHslTriplet(safePalette.card),
 '--card-foreground': toHslTriplet(safePalette.cardForeground),
 '--border': toHslTriplet(safePalette.border),
 '--primary': toHslTriplet(safePalette.accent),
 '--primary-foreground': toHslTriplet(safePalette.accentForeground),
 '--gv-on-accent': safePalette.accentForeground,
 '--secondary': toHslTriplet(safePalette.muted),
 '--secondary-foreground': toHslTriplet(safePalette.mutedForeground),
 '--muted': toHslTriplet(safePalette.muted),
 '--muted-foreground': toHslTriplet(safePalette.mutedForeground),
 backgroundColor: safePalette.card,
 color: safePalette.cardForeground,
 borderColor: safePalette.border,
 } as CSSProperties
 : undefined;

 const customer = session?.user as
 | { id?: string; name?: string | null; email?: string | null; image?: string | null; phoneNumber?: string | null }
 | undefined;

 const loadLoyaltyLinks = useCallback(async () => {
 try {
 const res = await customerFetch(
 businessId ? `/api/customer-auth/loyalty-links?businessId=${encodeURIComponent(businessId)}` : '/api/customer-auth/loyalty-links',
 );
 const data = await res.json();
 if (data.ok) { setLinked(data.linked || []); setCandidates(data.candidates || []); }
 } catch (err) { console.error('[customer] loadLoyaltyLinks failed', err); }
 }, [customerFetch, businessId]);

 const loadGiftCards = useCallback(async () => {
 try {
 const res = await customerFetch(
 businessId ? `/api/customer-auth/gift-cards?businessId=${encodeURIComponent(businessId)}` : '/api/customer-auth/gift-cards',
 );
 const data = await res.json();
 if (data.ok) setGiftCards(data.giftCards || []);
 } catch (err) { console.error('[customer] loadGiftCards failed', err); }
 }, [customerFetch, businessId]);

  const loadOrders = useCallback(async () => {
  try {
  const res = await customerFetch(
  businessId ? `/api/customer-auth/orders?businessId=${encodeURIComponent(businessId)}` : '/api/customer-auth/orders',
  );
  const data = await res.json();
  if (data.ok) setOrders(data.orders || []);
  } catch (err) { console.error('[customer] loadOrders failed', err); }
  }, [customerFetch, businessId]);

  // Sign-in completion: refresh this panel's data AND tell the rest of the
  // page (the ordering surface's session/contact) that auth changed, so it
  // updates immediately instead of waiting for a reload or the 5-minute
  // next-auth refetch interval. (MNQ customer-auth staleness fix.)
  const handleAuthenticated = useCallback(() => {
  void refresh();
  if (typeof window !== 'undefined') {
  window.dispatchEvent(new Event('mnrq:customer-session-changed'));
  }
  }, [refresh]);

  /**
   * The sign-in modal must not SURVIVE the sign-in it just completed.
   *
   * Auth success flips `status` to 'authenticated', which swaps this panel to
   * the signed-in branch and REMOUNTS CustomerAuthModal with the same
   * `authOpen` state — so the guest who just signed in was staring at the
   * "Sign in or create account" form forever, with no way through it except
   * closing the sheet (and the name-prompt flow never closed at all).
   *
   * The latch (`authOpenedSignedOutRef`) keeps the legitimate signed-in use of
   * this modal — opening it straight into passkey registration from the panel —
   * from being closed the instant it opens: only a modal opened while signed
   * OUT, whose sign-in then SUCCEEDS, is auto-closed.
   */
  const authOpenedSignedOutRef = useRef(false);
  useEffect(() => {
  if (authOpen && status !== 'authenticated') authOpenedSignedOutRef.current = true;
  if (authOpen && status === 'authenticated' && authOpenedSignedOutRef.current) {
  authOpenedSignedOutRef.current = false;
  setAuthOpen(false);
  }
  if (!authOpen) authOpenedSignedOutRef.current = false;
  }, [authOpen, status]);

 useEffect(() => {
 if (status !== 'authenticated') return;

 // Paint from the page-level cache, at ANY age. The panel used to start every
 // open with three sequential round trips and a blank sheet, and the fix for
 // that (lib/customer/account-prefetch.ts) still kept its cache in a `Map` — so
 // the reload that a QR-menu guest does most often put the blank sheet straight
 // back. The cache now survives the reload, so this paints synchronously.
 const warm = readAccountCache(businessId);
 if (warm) {
 setLinked(warm.linked as LinkedLoyaltyAccount[]);
 setCandidates(warm.candidates as LinkableLoyaltyAccount[]);
 setGiftCards(warm.giftCards as SavedGiftCard[]);
 setOrders(warm.orders as CustomerOrder[]);
 setInitialLoading(false);
 }

 // A missing snapshot has nothing to paint, so it must load before showing. A
 // stale one already has something on screen and refreshes behind it — the
 // sheet opens instantly either way, and a 60-second-old gift card list is far
 // less disruptive than a spinner.
 if (!warm) {
 setInitialLoading(true);
 void Promise.allSettled([loadLoyaltyLinks(), loadGiftCards(), loadOrders()]).finally(() => {
 setInitialLoading(false);
 });
 return;
 }

 if (isAccountCacheStale(businessId)) {
 void Promise.allSettled([loadLoyaltyLinks(), loadGiftCards(), loadOrders()]);
 }
 }, [status, businessId, loadLoyaltyLinks, loadGiftCards, loadOrders]);

 const saveGiftCard = useCallback(async () => {
 if (!giftCardCode.trim()) { toast.error('Enter a gift card code.'); return; }
 setGiftCardBusy('save');
 try {
 const res = await customerFetch('/api/customer-auth/gift-cards', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ code: giftCardCode }),
 });
 const data = await res.json();
 if (!data.ok) { toast.error(data.error || 'Could not save this gift card.'); return; }
 toast.success('Gift card saved.');
 setGiftCardCode('');
 await loadGiftCards();
 } catch (err) {
 toast.error('Could not save gift card. Please try again.');
 console.error('[customer] saveGiftCard failed', err);
 } finally { setGiftCardBusy(null); }
 }, [giftCardCode, loadGiftCards]);

 const removeGiftCard = useCallback(async (giftCardId: string) => {
 setGiftCardBusy(giftCardId);
 try {
 const res = await customerFetch(`/api/customer-auth/gift-cards/${giftCardId}`, { method: 'DELETE' });
 const data = await res.json();
 if (!data.ok) { toast.error(data.error || 'Could not remove this gift card.'); return; }
 await loadGiftCards();
 } catch (err) {
 toast.error('Could not remove gift card. Please try again.');
 console.error('[customer] removeGiftCard failed', err);
 } finally { setGiftCardBusy(null); }
 }, [loadGiftCards]);

 const confirmLink = useCallback(async (loyaltyAccountId: string) => {
 setConfirmingId(loyaltyAccountId);
 try {
 const res = await customerFetch('/api/customer-auth/loyalty-links/confirm', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ loyaltyAccountId }),
 });
 const data = await res.json();
 if (!data.ok) { toast.error(data.error || 'Could not link this account.'); return; }
 toast.success('Linked. Your points here now show up on your account.');
 await loadLoyaltyLinks();
 } catch (err) {
 toast.error('Could not link account. Please try again.');
 console.error('[customer] confirmLink failed', err);
 } finally { setConfirmingId(null); }
 }, [loadLoyaltyLinks]);

 // Sign-in/sign-up happens inline in the auth modal (which targets the
 // customer surface over the bridge so the session cookie lands on
 // c.menuraq.com); the signed-in data loads over the bridge too.

 // ── Loading skeleton ──
 if (status === 'authenticated' && customer && initialLoading) {
 return (
 <div className="flex flex-col gap-3 p-4">
 <div className="animate-pulse rounded-lg bg-[var(--color-bg-sunken)]/50 h-12 w-full" />
 <div className="animate-pulse rounded-lg bg-[var(--color-bg-sunken)]/50 h-24 w-full" />
 <div className="animate-pulse rounded-lg bg-[var(--color-bg-sunken)]/50 h-20 w-full" />
 </div>
 );
 }

 // ── Signed-in panel content ──
 if (status === 'authenticated' && customer) {
 return (
 <>
 {/* Sign-in modal (rarely opened while signed in) */}
 <CustomerAuthModal
 open={authOpen}
 onOpenChange={setAuthOpen}
 contentStyle={dialogStyle}
 overlayStyle={safePalette ? { backgroundColor: safePalette.overlay } : undefined}
  onAuthenticated={handleAuthenticated}
 chow={chow}
  />
  {/* Inline order tracker Dialog */}
 <Dialog open={trackedOrder !== null} onOpenChange={(open) => !open && setTrackedOrder(null)}>
 <DialogContent
 className="max-w-sm p-0 max-h-[86svh] overflow-y-auto overscroll-contain"
 style={dialogStyle}
 overlayStyle={safePalette ? { backgroundColor: safePalette.overlay } : undefined}
 >
 <DialogHeader className="px-4 pt-4 pb-0">
 <DialogTitle className="text-sm">Order status</DialogTitle>
 {trackedOrder && (
 <DialogDescription className="sr-only">
 Live order status for order {trackedOrder.id}
 </DialogDescription>
 )}
 </DialogHeader>
{trackedOrder && (
  <InlineOrderStatusContent
  orderId={trackedOrder.id}
  accessToken={trackedOrder.token}
  onStartOver={() => setTrackedOrder(null)}
  />
  )}
 </DialogContent>
 </Dialog>

 {/* Panel body */}
 <div className="flex flex-col gap-0 divide-y divide-border/60">
 {/* Profile header */}
 <div className="flex items-center gap-3 p-4">
 <Avatar className="h-11 w-11 shrink-0 border border-[var(--pub-border-subtle)]/70 shadow-sm">
 <AvatarImage src={customer.image || undefined} alt={customer.name || 'Account'} />
 <AvatarFallback className="text-2xs">{initialsFor(customer.name, customer.email)}</AvatarFallback>
 </Avatar>
 <div className="min-w-0 flex-1">
 <div className="flex min-w-0 items-center gap-2">
 <p className="truncate text-sm font-semibold">{customer.name || 'Customer'}</p>
 <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success-soft)] px-2 py-0.5 text-2xs font-semibold text-[var(--color-success)]">
 <ShieldCheck className="h-3 w-3" />
 Signed in
 </span>
 </div>
 {customer.email && <p className="truncate text-2xs text-[var(--color-text-secondary)]">{customer.email}</p>}
 {customer.phoneNumber && <p className="truncate text-2xs text-[var(--color-text-secondary)]">{customer.phoneNumber}</p>}
 </div>
 </div>

 {/* The "Customer dashboard" button was here. It pointed at customerUrl('/'),
     which is the API base as well as the navigation base and therefore resolves
     to `/` — the marketing homepage — on every non-production host. It was also
     visible only once signed in, and named after an internal concept rather
     than the product. Chow is now offered as its own row at the top of the You
     sheet, signed in or not, via `chowAppUrl`. One destination, one control. */}

 {/* Loyalty */}
 {(linked.length > 0 || candidates.length > 0) && (
 <div className="p-3 space-y-2">
 <p className="flex items-center gap-1.5 text-2xs font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
 <Gift className="h-3 w-3" /> Loyalty
 </p>
 {linked.map((acc) => (
 <div key={acc.businessId} className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm">
 <span className="truncate">{acc.businessName}</span>
 <span className="font-medium shrink-0 ml-2">{acc.balance} pts</span>
 </div>
 ))}
 {candidates.map((c) => (
 <div key={c.loyaltyAccountId} className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm">
 <span className="min-w-0 truncate">{c.businessName} · {c.balance} pts</span>
 <Button
 size="sm"
 variant="secondary"
 className="h-7 text-xs shrink-0"
 disabled={confirmingId === c.loyaltyAccountId}
 onClick={() => void confirmLink(c.loyaltyAccountId)}
 >
 {confirmingId === c.loyaltyAccountId ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Link'}
 </Button>
 </div>
 ))}
 </div>
 )}

 {/* Gift cards */}
 <div className="p-3 space-y-2">
 <div className="flex items-center justify-between gap-2">
 <p className="text-2xs font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">Gift cards</p>
 {giftCards.length > 0 ? (
 <span className="rounded-full bg-[var(--color-bg-sunken)] px-2 py-0.5 text-2xs font-medium text-[var(--color-text-secondary)]">
 {giftCards.length} saved
 </span>
 ) : null}
 </div>
 {giftCards.map((card) => (
 <div key={card.giftCardId} className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm">
 <span className="min-w-0 truncate">
 {card.businessName} · {card.balance} {card.currency || ''}
 {card.status === 'cancelled' ? ' (cancelled)' : ''}
 </span>
 <button
 type="button"
 aria-label="Remove saved gift card"
 disabled={giftCardBusy === card.giftCardId}
 onClick={() => {
 if (window.confirm(`Remove ${card.businessName} gift card?`)) {
 void removeGiftCard(card.giftCardId);
 }
 }}
 className="shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
 >
 {giftCardBusy === card.giftCardId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
 </button>
 </div>
 ))}
 {giftCards.length === 0 ? (
 <p className="rounded-xl border border-dashed px-3 py-3 text-left text-xs leading-5 text-[var(--color-text-secondary)]">
 Save a gift card once and it stays available whenever you sign in on this device.
 </p>
 ) : null}
 <div className="flex gap-2">
 <Input
 placeholder="Gift card code"
 value={giftCardCode}
 onChange={(e) => setGiftCardCode(e.target.value)}
 disabled={giftCardBusy !== null}
 className="h-8 text-sm"
 />
 <Button
 size="sm"
 className="h-8 shrink-0"
 disabled={giftCardBusy !== null}
 onClick={() => void saveGiftCard()}
 >
 {giftCardBusy === 'save' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
 </Button>
 </div>
 </div>

 {/* Recent orders */}
 {orders.length > 0 && (
 <div className="p-3 space-y-1.5">
 <p className="text-2xs font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">Recent orders</p>
 {orders.slice(0, 3).map((order) => (
 <button
 key={order.serviceOrderId}
 type="button"
 onClick={() => setTrackedOrder({ id: order.serviceOrderId, token: order.accessToken })}
 className="flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition hover:bg-[var(--color-bg-sunken)]/40"
 >
 <span className="min-w-0 truncate">{order.businessName} · {order.orderNumber}</span>
 <span className="shrink-0 ml-2 text-2xs text-[var(--color-text-secondary)] capitalize">{order.status}</span>
 </button>
 ))}
 {/* Chow's ORDERS page, not its root: "view all orders" that lands on a
     home screen makes the guest hunt for the thing they just asked for.
     `chowAppUrl` rather than `customerUrl` — the latter is the API base too,
     so it resolves to `/` (the marketing site) off production. */}
 {orders.length > 3 && (
 <a href={chowAppUrl('/orders')} className="flex items-center justify-center gap-1 rounded-xl border border-dashed px-3 py-2 text-sm text-[var(--color-text-secondary)] transition hover:bg-[var(--color-bg-sunken)]/40">
 View all {orders.length} orders in Chow
 <ArrowRight className="h-3.5 w-3.5" />
 </a>
 )}
 </div>
 )}

 {/* Sign out */}
 <div className="p-3">
 <Button
 variant="outline"
 size="sm"
 className="w-full text-sm"
 onClick={() => void signOut()}
 >
 <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
 </Button>
 </div>
 </div>
 </>
 );
 }

 // ── Signed-out panel content ──
 return (
 <>
 <CustomerAuthModal
 open={authOpen}
 onOpenChange={setAuthOpen}
 contentStyle={dialogStyle}
 overlayStyle={safePalette ? { backgroundColor: safePalette.overlay } : undefined}
  onAuthenticated={handleAuthenticated}
  chow={chow}
  />
  {/* Panel body when signed out */}
  <div className="flex flex-col items-center gap-4 p-6 text-center">
  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[var(--pub-border-subtle)] bg-[var(--color-bg-sunken)]/60 shadow-sm">
  <User className="h-6 w-6 text-[var(--color-text-secondary)]" />
  </div>
  <div>
  <p className="font-semibold text-base">Your account</p>
  <p className="mt-1 text-xs text-[var(--color-text-secondary)] leading-snug">
  Sign in to track orders, view loyalty points, and access gift cards across all Menuraq businesses.
  </p>
  {guestUsername ? (
  <p className="mt-1.5 text-2xs text-[var(--color-text-secondary)]">
  This device is browsing as @{guestUsername} , your handle once you sign up.
  </p>
  ) : null}
  </div>
 <div className="w-full space-y-2">
 <Button
 className="h-10 w-full"
 onClick={() => setAuthOpen(true)}
 >
 Sign in or create account
 </Button>
 </div>
 <p className="max-w-[15rem] text-2xs leading-4 text-[var(--color-text-secondary)]">
 Browse and order without signing in. Accounts are only for saved activity.
 </p>
 </div>
 </>
 );
}
